// VideoLinks — paste a URL, get a clickable chip with provider icon.
// Loom/YouTube get an inline embed toggle; every other link is just a link chip.
// Batch 1 · Feb 2026.

import { useState, useMemo } from "react";
import api from "../../lib/api";
import { Video, Play, X, Plus, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const PROVIDER_META = {
  loom:    { label: "Loom",    color: "text-purple-300", ring: "border-purple-400/40 bg-purple-500/10" },
  youtube: { label: "YouTube", color: "text-red-300",    ring: "border-red-400/40 bg-red-500/10" },
  drive:   { label: "Drive",   color: "text-sky-300",    ring: "border-sky-400/40 bg-sky-500/10" },
  other:   { label: "Video",   color: "text-slate-300",  ring: "border-white/10 bg-white/[0.03]" },
};

function embedUrl(url, provider) {
  try {
    const u = new URL(url);
    if (provider === "loom") {
      const parts = u.pathname.split("/").filter(Boolean);
      // Match Loom "share/<id>" or "record/<id>" patterns
      const id = parts.includes("share")  ? parts[parts.indexOf("share") + 1]
              : parts.includes("record") ? parts[parts.indexOf("record") + 1]
              : parts[parts.length - 1];
      return id ? `https://www.loom.com/embed/${id}` : null;
    }
    if (provider === "youtube") {
      if (u.hostname.includes("youtu.be")) {
        const id = u.pathname.replace("/", "");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch { /* fall through */ }
  return null;
}

export default function VideoLinks({ itemId, links = [], onChange }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});

  const add = async () => {
    if (!url.trim() || adding) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/items/${itemId}/video-links`, {
        url: url.trim(),
        label: label.trim(),
      });
      onChange && onChange([...(links || []), data]);
      setUrl(""); setLabel("");
    } finally { setAdding(false); }
  };
  const remove = async (link) => {
    await api.delete(`/items/${itemId}/video-links/${link.id}`);
    onChange && onChange((links || []).filter((l) => l.id !== link.id));
  };

  return (
    <div data-testid="video-links">
      <h4 className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5 mb-2">
        <Video className="w-3.5 h-3.5" /> Video links
        <span className="text-[10px] text-slate-500">({(links || []).length})</span>
      </h4>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="url"
          placeholder="Paste Loom / YouTube / Drive URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="input-dark flex-1 min-w-[200px] text-sm"
          data-testid="video-links-url"
        />
        <input
          type="text"
          placeholder="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="input-dark w-40 text-sm"
          data-testid="video-links-label"
        />
        <button
          type="button"
          onClick={add}
          disabled={adding || !url.trim()}
          className="btn-coral inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="video-links-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      {(links || []).length === 0 ? (
        <div className="text-[11px] text-slate-500 py-2">No videos yet.</div>
      ) : (
        <ul className="space-y-2">
          {(links || []).map((l) => (
            <VideoLinkRow
              key={l.id}
              link={l}
              expanded={!!expanded[l.id]}
              onToggle={() => setExpanded((s) => ({ ...s, [l.id]: !s[l.id] }))}
              onRemove={() => remove(l)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function VideoLinkRow({ link, expanded, onToggle, onRemove }) {
  const meta = PROVIDER_META[link.provider] || PROVIDER_META.other;
  const embed = useMemo(() => embedUrl(link.url, link.provider), [link.url, link.provider]);
  const canEmbed = link.provider === "loom" || link.provider === "youtube";

  return (
    <li className="rounded-md border border-white/5 bg-white/[0.02]" data-testid={`video-link-${link.id}`}>
      <div className="flex items-center gap-2 p-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-widest ${meta.ring} ${meta.color}`}>
          <Play className="w-2.5 h-2.5" />
          {meta.label}
        </span>
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-slate-100 hover:text-orange-300 flex-1 truncate"
          data-testid={`video-link-open-${link.id}`}
        >
          {link.label || link.url}
        </a>
        {canEmbed && embed && (
          <button
            type="button"
            onClick={onToggle}
            className="text-[10px] text-slate-400 hover:text-white inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/5"
            data-testid={`video-link-embed-toggle-${link.id}`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? "Hide" : "Preview"}
          </button>
        )}
        <a href={link.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white p-1" aria-label="Open in new tab">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button
          type="button"
          onClick={onRemove}
          className="text-rose-400/60 hover:text-rose-300 p-1"
          aria-label="Remove"
          data-testid={`video-link-remove-${link.id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && embed && (
        <div className="p-2 pt-0" data-testid={`video-link-embed-${link.id}`}>
          <div className="relative w-full pt-[56.25%] rounded-md overflow-hidden bg-black border border-white/5">
            <iframe
              src={embed}
              title={link.label || link.url}
              allow="fullscreen; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      )}
      {(link.added_by_name || link.added_at) && (
        <div className="px-2 pb-1.5 text-[10px] text-slate-500 font-mono">
          added by {link.added_by_name || "?"} · {link.added_at ? new Date(link.added_at).toLocaleString() : ""}
        </div>
      )}
    </li>
  );
}
